import { PRIME_APP } from '@/lib/appMode'
import { picturesToSend, type BlobRef, type InlinePicture } from '@/lib/blobs'
import type { PageVersion, PageVersionFull } from '@/lib/pageHistory'
import type { FeatureUsageReport } from '@/lib/usage'
import type { FixKey, FlowCommit, FlowLane, FlowPR } from '@/lib/flow'
import { isMember } from '@/state/access'
import type {
  FocusSessionInput,
  FocusSession,
  QueueItem,
  SearchHit,
  Weather,
  Place,
  Lyrics,
  HomeEntity,
  BrowseResult,
  PlayRequest,
  SpotifyDevice,
  AnalyticsQuery,
  Analytics,
  LastFMImport,
  Listening,
  SpotifyStatus,
  PlayerState,
  PlayerAction,
  LibraryHit,
  LibrarySources,
  LibraryItem,
  LibraryAdd,
  LibraryPatch,
  AgentNote,
  AgentStartRequest,
  AssistantAskRequest,
  BootstrapResponse,
  DeviceRequest,
  NowResponse,
  Op,
  PullResponse,
  PushResponse,
  Resource,
  ServerConfig,
  AgentToolResult,
  AgentVoice,
  MemoryItem,
  AuditArea,
  AuditQuery,
  AuditEvent,
  UsageReport,
  VoiceSession,
} from './types'

/** What a typed message carries beyond its text. */
export interface SendOpts {
  capture?: boolean
  /** the composer's pictures; agentSend sends them inline, or as blobs once UPLOAD_PICTURES_AS_BLOBS is on */
  images?: InlinePicture[]
}

export interface MemberThread {
  notice: string
  messages: {
    role: 'user' | 'assistant'
    content: string
    at: string
    prime?: boolean
    /** the buttons under a question; a tap sends the words as her reply */
    options?: string[]
    /** pages Prime showed beside the thread */
    pages?: string[]
  }[]
}

export class AuthError extends Error {
  constructor() {
    super('unauthorized')
    this.name = 'AuthError'
  }
}

/** statuses a proxy gives when the app itself did not answer */
const GATEWAY = new Set([502, 503, 504])

export class NetworkError extends Error {
  constructor(msg: string) {
    super(msg)
    this.name = 'NetworkError'
  }
}

/** Everything the sync engine needs from a server; FakeServer implements it for tests. */
/** GET /api/review/pr: a pull request and its diff, parsed. */
export interface ReviewLine {
  kind: '+' | '-' | ' '
  text: string
  old?: number
  new?: number
}
export interface ReviewFile {
  path: string
  old_path?: string
  status: 'added' | 'deleted' | 'renamed' | 'modified'
  additions: number
  deletions: number
  binary?: boolean
  truncated?: boolean
  hunks: { header: string; lines: ReviewLine[] }[]
}
export interface ReviewPull {
  pr: {
    number: number
    title: string
    body: string
    state: string
    merged: boolean
    mergeable?: boolean | null
    url: string
    head: string
    base: string
    author: string
    draft: boolean
    updated_at: string
  }
  files: ReviewFile[]
}

/** POST /api/factory/runs: hand a task to an agent. */
/** A build of main by the factory (GET /api/builds, docs/shipping.md). */
export interface Build {
  name: string
  repo: string
  sha: string
  tag: string
  note?: string
  by?: string
  state: 'queued' | 'building' | 'built' | 'failed'
  started: string
  finished?: string
  error?: string
}
/** An app published on the cluster (internal/api/apps.go, docs/publishing.md). */
export interface PublishedApp {
  name: string
  repo: string
  project_id?: string
  subdomain: string
  access: 'public' | 'tailnet'
  port: number
  health: string
  env?: Record<string, string>
  /** the build that runs; empty until one is chosen */
  tag?: string
  live: boolean
  updated_at?: string
}
/** A prod release: a tag on a built image; the first listed is live. */
export interface Release {
  tag: string
  sha: string
  at: string
  live: boolean
}
export interface Builds {
  repo: string
  builds: Build[]
  releases: Release[]
  /** what each environment's /healthz says it runs */
  versions: Record<string, string>
  /** what shipping now would take to prod: the newest build prod lacks, and the commits it carries */
  outgoing?: { build: Build; commits: { sha: string; subject: string }[] }
  /** prod's app as a ship moves through it (internal/cluster Rollout); absent off a cluster */
  rollout?: Rollout
}
/** GET /api/pipeline (internal/pipeline; the screen reads its own lanes with lib/flow.ts). */
export interface PipelineAnswer {
  now: string
  /** what GET /api/builds reads, main's commits prod lacks and the open PRs Forgejo was asked about */
  upstream: Builds & {
    main: FlowCommit[] | null
    main_at?: string
    checked?: FlowPR[]
  }
  lanes: FlowLane[]
  ship?: string
}
export interface PipelineMergeResult {
  item: string
  state?: 'queued' | 'merging' | 'merged'
  card?: string
  error?: string
}
export interface Rollout {
  at: string
  /** images Argo CD has applied */
  images: string[]
  sync: string
  health: string
  operation?: string
  operation_started?: string
  pods: {
    name: string
    image: string
    phase: string
    ready: boolean
    started?: string
    terminating?: boolean
    waiting?: string
  }[]
}
/** GET /api/cluster: the cluster, read-only (docs/prime-app.md). CPU and memory are shares in use, -1 unknown. */
export interface ClusterSummary {
  at: string
  nodes: {
    name: string
    ready: boolean
    roles?: string
    cpu: number
    memory: number
    pods: number
    version?: string
    pressure?: string
    /** the home node (label factory.alfiemills.co.uk/home) */
    home?: boolean
    /** agent sandboxes may run here (gVisor) */
    gvisor?: boolean
    arch?: string
    region?: string
  }[]
  pods: { running: number; total: number }
  problems: {
    namespace: string
    name: string
    node?: string
    phase: string
    reason?: string
    restarts?: number
  }[]
  apps: { name: string; sync: string; health: string }[]
  /** CI and build pods: Forgejo runners, GitHub (ARC) runners, the factory's image builds */
  runners?: ClusterRunner[]
  /** the agents' sandbox pods (agent-sandbox), running first, at most 30 */
  sandboxes?: ClusterSandbox[]
  /** every sandbox pod counted, before the list above is cut */
  sandbox_count?: { total: number; running: number; running_by_node: Record<string, number> }
  /** the image registry's pods */
  registry?: ClusterService[]
}
/** How one part of GET /api/infra answered: ok, off (its URL is not set) or failed (with why). */
export interface InfraSection {
  state: 'ok' | 'off' | 'failed'
  error?: string
}
export type AlertSeverity = 'critical' | 'warning' | 'info' | 'none'
/** GET /api/infra (internal/infra): what fires now and the security posture. Lists are capped; `more` says so. */
export interface InfraOverview {
  at: string
  /** Alertmanager's own UI, for the rest */
  alertmanager_url: string
  alerts: InfraSection & {
    groups: {
      alert: string
      /** the distinguishing label (rule, image, namespace) and its value */
      by?: string
      value?: string
      severity: AlertSeverity
      summary?: string
      since: string
      latest: string
      /** the machines its alerts name (host, node, or instance without its port), at most 6 */
      hosts?: string[]
    }[]
    more: boolean
  }
  images: InfraSection & {
    ours: { image: string; critical: number }[]
    theirs: { image: string; critical: number }[]
    ours_more: boolean
    theirs_more: boolean
  }
  policies: InfraSection & {
    policies: { policy: string; severity?: string; failing: number }[]
    more: boolean
  }
  falco: InfraSection & {
    rules: { rule: string; priority?: string; events: number; last: string }[]
    more: boolean
  }
}
/** A filesystem, a volume or a sum (GET /api/storage). Size 0 has no fixed size: only `used` means anything. */
export interface StorageDisk {
  name: string
  /** the device or the namespace */
  detail?: string
  size: number
  used: number
  objects?: number
  /** the SMART check of the drive it is on; absent when not read */
  smart?: 'ok' | 'failing'
}
export interface StorageHost {
  name: string
  /** NAS, Hypervisor, Cluster node, Cloud volumes, Storage Box, Bucket */
  role: string
  /** where a cluster node runs: "VM on proxmox2", a region */
  note?: string
  /** its exporter does not answer: no disks */
  down?: boolean
  disks: StorageDisk[]
  disks_more?: boolean
  /** SMART-checked drives no disk above is on */
  drives?: { name: string; healthy: boolean }[]
  /** the PVCs on its own disk, or on the Storage Box */
  volumes?: string[]
  volumes_more?: boolean
}
export interface StorageBackup {
  name: string
  /** mirror and snapshots are a home host's own (its plain copy, its dated copies) */
  kind: 'restic' | 'postgres' | 'mirror' | 'snapshots' | 'sync'
  where: string
  /** the home host a mirror, snapshots or offsite copy is of */
  host?: string
  /** the last good run and the last good offsite copy; absent is never */
  last?: string
  offsite?: string
  size?: number
  snapshots?: number
  stale: boolean
  offsite_stale?: boolean
}
/** GET /api/storage (internal/storage): what is stored where and the backups, from Prometheus. */
export interface StorageSummary {
  at: string
  /** critical first */
  warnings: { severity: 'critical' | 'warning'; text: string }[]
  sites: { id: 'home' | 'hetzner' | 'oracle' | 'other' | 'object'; name: string; hosts: StorageHost[] }[]
  backups: StorageBackup[]
}
/** How one section of GET /api/storage/inventory answered; `off` says why it is off. */
export interface StorageSection {
  state: 'ok' | 'off' | 'failed'
  /** prometheus: its URL unset; cluster: not running in one; rbac: the API server refuses the volumes */
  off?: 'prometheus' | 'cluster' | 'rbac'
  error?: string
}
/** A machine a drive or filesystem is on (GET /api/storage/inventory). */
export interface StorageMachine {
  name: string
  estate: Estate
  /** nas, hypervisor, host (a home host) or node (a cluster node) */
  role: string
  note?: string
  /** its exporter does not answer: nothing under it is known */
  down?: boolean
}
export interface PhysicalDrive {
  /** sda, nvme0n1, mmcblk0 */
  device: string
  model?: string
  kind?: 'nvme' | 'ssd' | 'hdd' | 'sd' | 'virtual' | 'volume'
  /** the filesystems on it added up, or its volume's size; absent unknown */
  size?: number
  /** absent where no smartmon runs */
  smart?: 'ok' | 'failing' | 'asleep'
  temperature?: number
  power_on_hours?: number
  /** the share of its rated endurance used, 0..1 (past 1 when worn beyond it) */
  wear?: number
  /** the claim (namespace/name) a Hetzner volume holds */
  volume?: string
}
export interface StorageFilesystem {
  mount: string
  type: string
  device?: string
  size: number
  used: number
}
export interface StorageShare {
  protocol: 'nfs' | 'smb'
  name: string
  path?: string
  access?: 'rw' | 'ro'
  guest?: boolean
  /** the hosts an NFS export lets in, named where known */
  allowed?: string[]
  /** connected now; absent when not reported */
  clients?: number
}
export type VolumeWhere = 'cloud' | 'box' | 'nfs' | 'local' | 'other'
export interface StorageVolume {
  namespace?: string
  /** absent for a volume no claim holds */
  claim?: string
  pv?: string
  class?: string
  where: VolumeWhere
  capacity: number
  /** only for a volume that is its own disk (a cloud volume) */
  used?: number
  node?: string
  /** a local path, or an NFS export */
  source?: string
  phase: string
  reclaim?: string
  /** a best guess by name: covered, or unknown (never "not backed up") */
  backup: 'covered' | 'unknown'
  backed_up_by?: string[]
}
/** GET /api/storage/inventory (internal/storage): the infra pack's Storage page, a state per section. */
export interface StorageInventory {
  at: string
  drives: StorageSection & { hosts: (StorageMachine & { drives: PhysicalDrive[] })[] }
  filesystems: StorageSection & { hosts: (StorageMachine & { filesystems: StorageFilesystem[] })[] }
  shares: StorageSection & { hosts: { name: string; shares: StorageShare[]; nfs_clients: string[] }[] }
  volumes: StorageSection & {
    volumes: StorageVolume[]
    classes: { name: string; provisioner: string; reclaim?: string; default?: boolean }[]
  }
  cloud: StorageSection & { stores: StorageHost[] }
  backups: StorageSection & { backups: StorageBackup[] }
}
export type Estate = 'hetzner' | 'home' | 'oracle' | 'other'
/** A filesystem on a host (GET /api/hosts). */
export interface HostDisk {
  mount: string
  size: number
  used: number
}
/** One machine (GET /api/hosts). Up is whether its exporter answers; the figures are there only when it does. */
export interface Host {
  name: string
  estate: Estate
  /** nas, hypervisor (home hosts); control-plane, media, node (cluster nodes); host when unlabelled */
  role: string
  /** where a cluster node runs: "VM 110 on proxmox2", a region */
  note?: string
  /** the scrape target without its port, which an alert's instance names it by */
  address?: string
  up: boolean
  /** seconds since boot */
  uptime?: number
  /** busy over 5 minutes and memory in use, 0..1 */
  cpu?: number
  memory?: number
  /** the 5-minute load average per core */
  load?: number
  cores?: number
  root?: HostDisk
  /** the fullest filesystem (it may be the root) */
  worst?: HostDisk
  /** absent where no smartmon runs (the cluster's VMs) */
  smart?: 'ok' | 'failing' | 'unknown'
  smart_failing?: string[]
  /** absent where nothing reports it */
  reboot_required?: boolean
}
/** GET /api/hosts (internal/hosts): every machine, from Prometheus; facts only, lib/hosts.ts judges them. */
export interface HostsSummary {
  at: string
  hosts: Host[]
}
export interface ClusterSandbox {
  name: string
  /** the factory's template: code, prime … ("sandbox" when unlabelled) */
  template: string
  node?: string
  phase: string
  ready: boolean
  restarts?: number
  since?: string
  reason?: string
}
export interface ClusterService {
  namespace: string
  name: string
  node?: string
  phase: string
  ready: boolean
  restarts: number
  /** when the running container started */
  since?: string
  /** the last restart's reason and exit code */
  last_exit?: string
  last_exit_at?: string
}
export interface ClusterRunner {
  kind: 'forgejo' | 'github' | 'build'
  namespace: string
  name: string
  node?: string
  phase: string
  ready: boolean
  restarts?: number
  since?: string
  image?: string
}
/** GET /api/agents/stats: spend is API-equivalent when `notional` (a subscription pays). */
export interface AgentStats {
  days: number
  notional: boolean
  totals: {
    usd: number
    lead_usd: number
    agents_usd: number
    turns: number
    started: number
    prs_opened: number
    prs_merged: number
  }
  by_day: { date: string; lead_usd: number; agents_usd: number; turns: number }[]
  top: { id: string; title: string; lead: boolean; usd: number; turns: number; phase: string; pr?: number }[]
}

/** One of Prime's watches (assistant.LeadWatch). */
export interface LeadWatch {
  id: string
  on: string
  target: string
  label: string
  message: string
  then: 'notify' | 'wake' | 'both'
  made: string
}

export interface FactoryHandOff {
  /** a task to hand over; without one the session starts from scratch */
  task_id?: string
  repo: string
  prompt: string
  title?: string
  template?: 'code' | 'ops'
  /** the workspace it belongs to (a task's own wins); the connector's when left out */
  workspace?: string
  /** the project whose context it gets (notes, docs, open tasks); found from the repo when left out */
  project_id?: string
  /** the agent session that started it: woken when the agent finishes */
  session?: string
  /** the model: Sonnet unless Opus is asked for (Prime picks per task) */
  model?: 'opus' | 'sonnet'
}

export interface ApiClient {
  push(ops: Op[]): Promise<PushResponse>
  pull(since: number, limit?: number): Promise<PullResponse>
  bootstrap(): Promise<BootstrapResponse>
  registerDevice(req: DeviceRequest): Promise<void>
  /** drop a device row and its push subscription; a device in use registers itself again */
  forgetDevice(id: string): Promise<void>
  /** the library is online only: nothing here touches Dexie or the sync engine */
  libraryList(q?: {
    kind?: string
    status?: string
    q?: string
  }): Promise<{ items: LibraryItem[]; sources: LibrarySources }>
  librarySearch(kind: string, q: string): Promise<{ hits: LibraryHit[]; sources: LibrarySources }>
  librarySyncSteam(): Promise<{ games: number; added: number; updated: number; points: number }>
  libraryListening(days?: number): Promise<Listening>
  libraryAnalytics(q: AnalyticsQuery): Promise<Analytics>
  libraryBrowse(
    kind: 'artists' | 'albums' | 'tracks' | 'all',
    q?: string,
    limit?: number,
  ): Promise<BrowseResult>
  spotifyEnrich(): Promise<void>
  spotifyStatus(): Promise<SpotifyStatus>
  spotifyConnect(): Promise<{ url: string }>
  spotifyCallback(state: string, code: string): Promise<SpotifyStatus>
  spotifyImport(
    files: File[],
  ): Promise<{ files: number; plays: number; tracks: number; skipped: number; already: number }>
  spotifyImportLastFM(user: string, key: string): Promise<LastFMImport>
  spotifyClearImport(source: 'lastfm' | 'spotify'): Promise<{ removed: number }>
  spotifyDisconnect(): Promise<void>
  spotifySync(): Promise<{ plays: number; tracks: number; playlists: number }>
  spotifyPlayer(): Promise<PlayerState>
  spotifyControl(action: PlayerAction, play?: PlayRequest): Promise<PlayerState>
  spotifyDevices(): Promise<{ devices: SpotifyDevice[] }>
  spotifyQueue(): Promise<{ connected: boolean; queue: QueueItem[] }>
  spotifySearch(q: string): Promise<{ connected: boolean; hits: SearchHit[] }>
  weather(lat: number, lon: number, units?: string): Promise<Weather>
  geocode(q: string): Promise<{ places: Place[] }>
  lyrics(artist: string, title: string, album?: string, durationSec?: number): Promise<Lyrics>
  homeStates(ids?: string[]): Promise<{ configured: boolean; entities: HomeEntity[] }>
  homeCall(entityId: string, service?: string, data?: Record<string, unknown>): Promise<void>
  /** hand a task to the software factory; the run arrives as an AgentRun mirror (id) */
  factoryHandOff(req: FactoryHandOff): Promise<{ id: string; name: string }>
  /** one attempt's Claude Code stream (online only) */
  factoryTranscript(name: string, attempt?: number): Promise<{ lines: string[] }>
  /** a pull request with its diff parsed into files (online only) */
  reviewPull(repo: string, number: number): Promise<ReviewPull>
  /** what Prime and the coding agents spent and did over the last N days (online only) */
  agentStats(days: number): Promise<AgentStats>
  /** the factory's builds of main, the prod releases and what each environment runs */
  builds(): Promise<Builds>
  /** every change from a ready pull request to prod, by lane, and what it was read from (online only) */
  pipeline(): Promise<PipelineAnswer>
  /** the safe fix the pipeline offers for a stuck item; what was done, in a sentence */
  pipelineFix(item: string, fix: FixKey): Promise<{ done: string }>
  /** Merge, Merge all and Merge and ship (ship): each ready pull request into the merge queue, in order */
  pipelineMerge(items: string[], ship?: boolean): Promise<{ results: PipelineMergeResult[] }>
  /** the published apps (online only) */
  apps(): Promise<{ apps: PublishedApp[] }>
  /** publish an app, or change how it is published */
  appPut(name: string, app: Omit<PublishedApp, 'name' | 'live' | 'tag'>): Promise<PublishedApp>
  /** run a built version of its repo and make it live */
  usage(): Promise<FeatureUsageReport>
  usageRecord(hits: { feature: string; n: number }[], device: 'phone' | 'desktop'): Promise<void>
  usageSwitch(enabled: boolean): Promise<{ enabled: boolean; since: string }>
  usageClear(): Promise<{ removed: number }>
  appShip(name: string, tag: string): Promise<PublishedApp>
  appUnpublish(name: string): Promise<PublishedApp>
  appDelete(name: string): Promise<void>
  /** build the repo's main as it is now */
  appBuild(name: string): Promise<Build>
  /** one build with the last lines of its log */
  buildLog(name: string): Promise<{ build: Build; log: string }>
  /** build (or retry) a commit on main */
  buildStart(sha: string): Promise<Build>
  /** ship a built commit to prod */
  release(sha: string): Promise<Release>
  /** the cluster this server runs in, read-only */
  cluster(): Promise<ClusterSummary>
  /** what fires now in Alertmanager and the security posture in Prometheus (master-only, online-only) */
  infra(): Promise<InfraOverview>
  /** what is stored where and the backups, from Prometheus (master-only, online-only) */
  storage(): Promise<StorageSummary>
  /** every machine with its load and disks, from Prometheus (master-only, online-only) */
  hosts(): Promise<HostsSummary>
  /** every drive, filesystem, share, volume, cloud store and backup (master-only, online-only) */
  storageInventory(): Promise<StorageInventory>
  /** a page's versions from the server's changelog, newest first (master-only, online-only) */
  pageHistory(id: string): Promise<{ versions: PageVersion[] }>
  /** one version in full */
  pageVersion(id: string, seq: number): Promise<PageVersionFull>
  /** where Agent Prime on Claude Code is (its AgentRun), and whether it runs */
  lead(): Promise<{ run_id: string; live: boolean }>
  /** Prime's watches waiting to fire ("when … then …") */
  leadWatches(): Promise<{ watches: LeadWatch[] }>
  /** cancel one of Prime's watches */
  leadUnwatch(id: string): Promise<void>
  /** Prime files away what matters (memory) and hands over to a fresh session (background) */
  leadFresh(): Promise<{ status: string }>
  /** start Agent Prime on Claude Code (or return the one running) */
  leadStart(): Promise<{ run_id: string; live: boolean }>
  /** ask Prime to look at a coding agent now; the review lands on the thread returned */
  factoryReview(id: string, session?: string): Promise<{ session: string }>
  /** a session's lines after a seq, straight from the factory (online only) */
  factoryConversation(
    name: string,
    after: number,
  ): Promise<{
    entries: { seq: number; at?: string; role: string; text: string; pictures?: string[] }[]
    phase: string
  }>
  /** a message with pictures to a coding agent or Prime's lead session (online only; words alone are the `say` action) */
  factorySend(name: string, text: string, images: { media_type: string; data: string }[]): Promise<void>
  /** one picture of a session as bytes, for an <img> via a blob URL (it needs our token) */
  factoryPicture(name: string, file: string): Promise<Blob>
  /** run the session's app in its sandbox; the signed link into it (agent-factory README "Previews") */
  factoryPreview(name: string, to?: string): Promise<{ url: string; state: string }>
  factoryPreviewStop(name: string): Promise<void>
  /** a project's "Try it": its live try session on main (started if need be) and a preview link to it */
  factoryTry(projectId: string): Promise<{ id: string; name: string; url: string; state: string }>
  /** set up a prototype: a private repo from the template (api/prototypes.go) */
  factoryPrototype(name: string, description: string): Promise<{ repo: string; url: string }>
  /** keep a picture or file on the server (online only); the id is the sha256 of its bytes */
  putBlob(file: Blob): Promise<BlobRef>
  /** a kept blob's bytes, for an <img> via a blob URL (it needs our token) */
  blob(id: string): Promise<Blob>
  reviewSubmit(
    repo: string,
    number: number,
    kind: 'approve' | 'request_changes' | 'comment',
    body: string,
  ): Promise<void>
  /** card: a card replaying its merge, which records its own answer (else the server settles the cards) */
  reviewMerge(
    repo: string,
    number: number,
    style?: 'squash' | 'merge' | 'rebase',
    card?: boolean,
  ): Promise<void>
  /** put a merge card in the merge queue; where it stands (a second tap, or a merged card, answers the same) */
  mergeQueue(suggestionId: string): Promise<{ state: 'queued' | 'merging' | 'merged' }>
  /** ask Prime for a walkthrough page of a PR (409 when no lead runs) */
  reviewWalkthrough(repo: string, number: number): Promise<void>
  voiceSession(): Promise<VoiceSession>
  voiceVoices(): Promise<{ voices: AgentVoice[]; current: string }>
  /** the sample as bytes from this origin, for an <audio> via a blob URL */
  voicePreview(id: string): Promise<Blob>
  /** a share link's preview card drawn for words not saved yet */
  previewCard(card: { title: string; sub: string; icon?: string }): Promise<Blob>
  voiceSetVoice(id: string): Promise<{ voice_id?: string; error?: string }>
  voiceMemory(): Promise<{ items: MemoryItem[] }>
  assistantUsage(): Promise<UsageReport>
  audit(q: AuditQuery): Promise<{ events: AuditEvent[] }>
  auditAreas(): Promise<{ areas: AuditArea[] }>
  /** a device-side event for the trail: a tool the desk ran, an error it saw */
  auditPost(e: {
    level?: string
    area: string
    action: string
    subject?: string
    message: string
    attrs?: Record<string, unknown>
  }): Promise<void>
  voiceRemember(text: string): Promise<{ items: MemoryItem[] }>
  voiceForget(at: string): Promise<{ items: MemoryItem[] }>
  /** hand a finished conversation over to be summarised into memory */
  voiceSummarise(transcript: { role: 'user' | 'assistant'; text: string }[]): Promise<void>
  capture(text: string, from?: string): Promise<{ ok: boolean; task: Resource }>
  focusRecord(session: FocusSessionInput): Promise<void>
  focusSessions(days?: number): Promise<{ sessions: FocusSession[] }>
  libraryAdd(item: LibraryAdd): Promise<{ item: LibraryItem }>
  libraryPatch(id: string, patch: LibraryPatch): Promise<{ item: LibraryItem }>
  libraryRemove(id: string): Promise<void>
  pushTest(deviceId?: string): Promise<void>
  now(): Promise<NowResponse>
  config(): Promise<ServerConfig>
  export(): Promise<unknown>
  assistantAsk(req: AssistantAskRequest): Promise<{ suggestions: Resource[]; text?: string }>
  agentStart(req: AgentStartRequest): Promise<{ session: Resource }>
  /** the session comes back without its pictures (a thread keeps up to 24 MB); sync for the record */
  agentSend(sessionId: string, content: string, opts?: SendOpts): Promise<{ session: Resource }>
  /** the device ran the tools the assistant asked for; the turn carries on */
  agentToolResults(sessionId: string, results: AgentToolResult[]): Promise<{ session: Resource }>
  /** the one long-lived thread for a workspace, made on first ask */
  agentPrime(workspace: string): Promise<{ session: Resource }>
  /**
   * Hold open the server's event stream and call onSeq for every committed
   * changelog seq until the signal aborts. Resolves when the stream ends.
   */
  events(onSeq: (seq: number) => void, signal: AbortSignal, onAgent?: (n: AgentNote) => void): Promise<void>
}

export class HttpApi implements ApiClient {
  constructor(
    private tokenProvider: () => Promise<string | undefined>,
    private base = '',
    private fetchFn: typeof fetch = (...a) => fetch(...a),
    /** this device's id, sent so the server can record when it was last seen */
    private deviceProvider: () => Promise<string | undefined> = async () => undefined,
  ) {}

  private async req<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
    const token = await this.tokenProvider()
    if (!token) throw new AuthError()
    const device = await this.deviceProvider().catch(() => undefined)
    let res: Response
    try {
      res = await this.fetchFn(this.base + path, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(device ? { 'X-Device-Id': device } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      })
    } catch (e) {
      throw new NetworkError((e as Error).message)
    }
    if (res.status === 401) throw new AuthError()
    if (!res.ok) {
      // the server's reason, when it gave one: "no Spotify device is open" beats "502"
      let reason = ''
      try {
        reason = String(((await res.json()) as { error?: string }).error ?? '')
      } catch {
        /* not JSON */
      }
      // a gateway status with no reason is the proxy, not the app: a rollout's
      // dead upstream, a stale keep-alive. A read is safe to ask once more.
      if (!reason && GATEWAY.has(res.status)) {
        if (method === 'GET' && !retried) {
          await new Promise((r) => setTimeout(r, 800))
          return this.req<T>(method, path, body, true)
        }
        throw new NetworkError(`the server did not answer (${res.status})`)
      }
      throw new NetworkError(reason || `${method} ${path}: ${res.status}`)
    }
    return (await res.json()) as T
  }

  push(ops: Op[]) {
    return this.req<PushResponse>('POST', '/api/sync/push', { ops })
  }
  async events(
    onSeq: (seq: number) => void,
    signal: AbortSignal,
    onAgent?: (n: AgentNote) => void,
  ): Promise<void> {
    const token = await this.tokenProvider()
    if (!token) throw new AuthError()
    let res: Response
    try {
      res = await this.fetchFn(this.base + '/api/events', {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
        signal,
      })
    } catch (e) {
      if (signal.aborted) return
      throw new NetworkError((e as Error).message)
    }
    if (res.status === 401) throw new AuthError()
    if (!res.ok || !res.body) throw new NetworkError(`GET /api/events: ${res.status}`)
    // a minimal SSE reader: "event:" names the kind, "data:" carries it, blank lines end it
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    let kind = 'seq'
    try {
      for (;;) {
        const { value, done } = await reader.read()
        if (done) return
        buf += decoder.decode(value, { stream: true })
        let nl: number
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).replace(/\r$/, '')
          buf = buf.slice(nl + 1)
          if (line.startsWith('event:')) {
            kind = line.slice(6).trim()
            continue
          }
          if (!line.startsWith('data:')) continue
          const body = line.slice(5).trim()
          try {
            if (kind === 'agent') onAgent?.(JSON.parse(body) as AgentNote)
            else {
              const seq = (JSON.parse(body) as { seq?: number }).seq
              if (typeof seq === 'number') onSeq(seq)
            }
          } catch {
            /* a malformed line is not worth dropping the stream for */
          }
        }
      }
    } catch (e) {
      if (signal.aborted) return
      throw new NetworkError((e as Error).message)
    }
  }
  pull(since: number, limit = 500) {
    return this.req<PullResponse>('GET', `/api/sync/pull?since=${since}&limit=${limit}`)
  }
  bootstrap() {
    return this.req<BootstrapResponse>('GET', '/api/sync/bootstrap')
  }
  registerDevice(req: DeviceRequest) {
    // a workspace member has a door of her own: her device, under her grant, for her pushes
    if (isMember()) return this.req<void>('POST', '/api/member/device', req)
    // the Prime app is a device of its own, and Prime's notifications go to it
    return this.req<void>('POST', '/api/devices', PRIME_APP ? { ...req, app: 'prime' as const } : req)
  }
  /** A workspace member's own Prime: her thread, and one turn of it (assistant.MemberAsk). */
  memberPrime() {
    return this.req<MemberThread>('GET', '/api/member/prime')
  }
  memberPrimeSend(message: string) {
    return this.req<MemberThread>('POST', '/api/member/prime', { message })
  }
  forgetDevice(id: string) {
    return this.req<void>('DELETE', `/api/devices/${encodeURIComponent(id)}`)
  }
  libraryList(q: { kind?: string; status?: string; q?: string } = {}) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v) params.set(k, v)
    const qs = params.toString()
    return this.req<{ items: LibraryItem[]; sources: LibrarySources }>(
      'GET',
      `/api/library${qs ? `?${qs}` : ''}`,
    )
  }
  librarySearch(kind: string, q: string) {
    const params = new URLSearchParams({ kind, q })
    return this.req<{ hits: LibraryHit[]; sources: LibrarySources }>('GET', `/api/library/search?${params}`)
  }
  librarySyncSteam() {
    return this.req<{ games: number; added: number; updated: number; points: number }>(
      'POST',
      '/api/library/sync/steam',
    )
  }
  libraryListening(days = 30) {
    return this.req<Listening>('GET', `/api/library/listening?days=${days}`)
  }
  libraryAnalytics(q: AnalyticsQuery) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') params.set(k, String(v))
    return this.req<Analytics>('GET', `/api/library/analytics?${params}`)
  }
  libraryBrowse(kind: 'artists' | 'albums' | 'tracks' | 'all', q = '', limit = 200) {
    const params = new URLSearchParams({ kind, limit: String(limit) })
    if (q) params.set('q', q)
    return this.req<BrowseResult>('GET', `/api/library/browse?${params}`)
  }
  spotifyEnrich() {
    return this.req<void>('POST', '/api/spotify/enrich')
  }
  spotifyStatus() {
    return this.req<SpotifyStatus>('GET', '/api/spotify')
  }
  spotifyConnect() {
    return this.req<{ url: string }>('POST', '/api/spotify/connect')
  }
  spotifyCallback(state: string, code: string) {
    return this.req<SpotifyStatus>('POST', '/api/spotify/callback', { state, code })
  }
  spotifyImportLastFM(user: string, key: string) {
    return this.req<LastFMImport>('POST', '/api/spotify/import/lastfm', { user, key })
  }
  spotifyClearImport(source: 'lastfm' | 'spotify') {
    return this.req<{ removed: number }>('POST', '/api/spotify/import/clear', { source })
  }
  /** the data export as Spotify sent it; multipart, so not through req() */
  async spotifyImport(files: File[]) {
    const token = await this.tokenProvider()
    if (!token) throw new AuthError()
    const form = new FormData()
    for (const f of files) form.append('files', f, f.name)
    let res: Response
    try {
      res = await this.fetchFn(this.base + '/api/spotify/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
    } catch (e) {
      throw new NetworkError((e as Error).message)
    }
    if (res.status === 401) throw new AuthError()
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { error?: string }
      throw new NetworkError(j.error ?? `import: ${res.status}`)
    }
    return (await res.json()) as {
      files: number
      plays: number
      tracks: number
      skipped: number
      already: number
    }
  }
  spotifyDisconnect() {
    return this.req<void>('POST', '/api/spotify/disconnect')
  }
  spotifySync() {
    return this.req<{ plays: number; tracks: number; playlists: number }>('POST', '/api/spotify/sync')
  }
  spotifyPlayer() {
    return this.req<PlayerState>('GET', '/api/spotify/player')
  }
  spotifyControl(action: PlayerAction, play?: PlayRequest) {
    return this.req<PlayerState>('POST', '/api/spotify/player', { action, ...(play ?? {}) })
  }
  spotifyDevices() {
    return this.req<{ devices: SpotifyDevice[] }>('GET', '/api/spotify/devices')
  }
  spotifyQueue() {
    return this.req<{ connected: boolean; queue: QueueItem[] }>('GET', '/api/spotify/queue')
  }
  spotifySearch(q: string) {
    return this.req<{ connected: boolean; hits: SearchHit[] }>(
      'GET',
      `/api/spotify/search?${new URLSearchParams({ q })}`,
    )
  }
  weather(lat: number, lon: number, units = 'celsius') {
    return this.req<Weather>(
      'GET',
      `/api/weather?${new URLSearchParams({ lat: String(lat), lon: String(lon), units })}`,
    )
  }
  geocode(q: string) {
    return this.req<{ places: Place[] }>('GET', `/api/weather/geocode?${new URLSearchParams({ q })}`)
  }
  lyrics(artist: string, title: string, album = '', durationSec = 0) {
    const p = new URLSearchParams({ artist, title })
    if (album) p.set('album', album)
    if (durationSec) p.set('duration', String(durationSec))
    return this.req<Lyrics>('GET', `/api/lyrics?${p}`)
  }
  voiceSession() {
    return this.req<VoiceSession>('GET', '/api/voice/session')
  }
  voiceVoices() {
    return this.req<{ voices: AgentVoice[]; current: string }>('GET', '/api/voice/voices')
  }
  async previewCard(card: { title: string; sub: string; icon?: string }) {
    const token = await this.tokenProvider()
    if (!token) throw new AuthError()
    const r = await this.fetchFn(`${this.base}/api/preview/card`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(card),
    })
    if (!r.ok) throw new Error(`preview card: ${r.status}`)
    return r.blob()
  }
  async voicePreview(id: string) {
    const token = await this.tokenProvider()
    if (!token) throw new AuthError()
    const r = await this.fetchFn(`${this.base}/api/voice/voices/${encodeURIComponent(id)}/preview`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!r.ok) throw new Error(`preview: ${r.status}`)
    return r.blob()
  }
  voiceSetVoice(id: string) {
    return this.req<{ voice_id?: string; error?: string }>('PUT', '/api/voice/settings', { voice_id: id })
  }
  voiceMemory() {
    return this.req<{ items: MemoryItem[] }>('GET', '/api/voice/memory')
  }
  assistantUsage() {
    return this.req<UsageReport>('GET', '/api/assistant/usage')
  }
  audit(q: AuditQuery) {
    const p = new URLSearchParams()
    if (q.area) p.set('area', q.area)
    if (q.level) p.set('level', q.level)
    if (q.q) p.set('q', q.q)
    if (q.since) p.set('since', q.since)
    if (q.before) p.set('before', String(q.before))
    if (q.limit) p.set('limit', String(q.limit))
    const qs = p.toString()
    return this.req<{ events: AuditEvent[] }>('GET', `/api/audit${qs ? `?${qs}` : ''}`)
  }
  auditAreas() {
    return this.req<{ areas: AuditArea[] }>('GET', '/api/audit/areas')
  }
  async auditPost(e: {
    level?: string
    area: string
    action: string
    subject?: string
    message: string
    attrs?: Record<string, unknown>
  }) {
    await this.req<unknown>('POST', '/api/audit', e)
  }
  voiceRemember(text: string) {
    return this.req<{ items: MemoryItem[] }>('POST', '/api/voice/memory', { text })
  }
  voiceForget(at: string) {
    return this.req<{ items: MemoryItem[] }>('DELETE', `/api/voice/memory?at=${encodeURIComponent(at)}`)
  }
  async voiceSummarise(transcript: { role: 'user' | 'assistant'; text: string }[]) {
    await this.req<unknown>('POST', '/api/voice/remember', { transcript })
  }
  homeStates(ids: string[] = []) {
    return this.req<{ configured: boolean; entities: HomeEntity[] }>(
      'GET',
      `/api/home/states${ids.length ? `?ids=${encodeURIComponent(ids.join(','))}` : ''}`,
    )
  }
  homeCall(entityId: string, service = 'toggle', data?: Record<string, unknown>) {
    return this.req<void>('POST', '/api/home/call', { entity_id: entityId, service, data })
  }
  factoryHandOff(req: FactoryHandOff) {
    return this.req<{ id: string; name: string }>('POST', '/api/factory/runs', req)
  }
  lead() {
    return this.req<{ run_id: string; live: boolean }>('GET', '/api/lead')
  }
  agentStats(days: number) {
    return this.req<AgentStats>('GET', `/api/agents/stats?days=${days}`)
  }
  apps() {
    return this.req<{ apps: PublishedApp[] }>('GET', '/api/apps')
  }
  appPut(name: string, app: Omit<PublishedApp, 'name' | 'live' | 'tag'>) {
    return this.req<PublishedApp>('PUT', `/api/apps/${encodeURIComponent(name)}`, app)
  }
  usage() {
    return this.req<FeatureUsageReport>('GET', '/api/usage')
  }
  async usageRecord(hits: { feature: string; n: number }[], device: 'phone' | 'desktop') {
    await this.req<unknown>('POST', '/api/usage', { device, hits })
  }
  usageSwitch(enabled: boolean) {
    return this.req<{ enabled: boolean; since: string }>('PUT', '/api/usage', { enabled })
  }
  usageClear() {
    return this.req<{ removed: number }>('DELETE', '/api/usage')
  }
  appShip(name: string, tag: string) {
    return this.req<PublishedApp>('POST', `/api/apps/${encodeURIComponent(name)}/ship`, { tag })
  }
  appUnpublish(name: string) {
    return this.req<PublishedApp>('POST', `/api/apps/${encodeURIComponent(name)}/unpublish`, {})
  }
  async appDelete(name: string) {
    await this.req<void>('DELETE', `/api/apps/${encodeURIComponent(name)}`)
  }
  appBuild(name: string) {
    return this.req<Build>('POST', `/api/apps/${encodeURIComponent(name)}/build`, {})
  }
  builds() {
    return this.req<Builds>('GET', '/api/builds')
  }
  pipeline() {
    return this.req<PipelineAnswer>('GET', '/api/pipeline')
  }
  pipelineFix(item: string, fix: FixKey) {
    return this.req<{ done: string }>('POST', '/api/pipeline/fix', { item, fix })
  }
  pipelineMerge(items: string[], ship = false) {
    return this.req<{ results: PipelineMergeResult[] }>(
      'POST',
      '/api/pipeline/merge',
      ship ? { items, ship } : { items },
    )
  }
  buildLog(name: string) {
    return this.req<{ build: Build; log: string }>('GET', `/api/builds/${encodeURIComponent(name)}`)
  }
  buildStart(sha: string) {
    return this.req<Build>('POST', '/api/builds', { sha })
  }
  release(sha: string) {
    return this.req<Release>('POST', '/api/releases', { sha })
  }
  cluster() {
    return this.req<ClusterSummary>('GET', '/api/cluster')
  }
  infra() {
    return this.req<InfraOverview>('GET', '/api/infra')
  }
  storage() {
    return this.req<StorageSummary>('GET', '/api/storage')
  }
  hosts() {
    return this.req<HostsSummary>('GET', '/api/hosts')
  }
  storageInventory() {
    return this.req<StorageInventory>('GET', '/api/storage/inventory')
  }
  pageHistory(id: string) {
    return this.req<{ versions: PageVersion[] }>('GET', `/api/pages/${id}/history`)
  }
  pageVersion(id: string, seq: number) {
    return this.req<PageVersionFull>('GET', `/api/pages/${id}/history?seq=${seq}`)
  }
  leadWatches() {
    return this.req<{ watches: LeadWatch[] }>('GET', '/api/lead/watches')
  }
  leadUnwatch(id: string) {
    return this.req<void>('DELETE', `/api/lead/watches/${encodeURIComponent(id)}`)
  }
  leadFresh() {
    return this.req<{ status: string }>('POST', '/api/lead/fresh', {})
  }
  leadStart() {
    return this.req<{ run_id: string; live: boolean }>('POST', '/api/lead/start', {})
  }
  factoryReview(id: string, session?: string) {
    return this.req<{ session: string }>(
      'POST',
      `/api/factory/runs/${encodeURIComponent(id)}/review`,
      session ? { session } : {},
    )
  }
  factoryConversation(name: string, after: number) {
    return this.req<{
      entries: { seq: number; at?: string; role: string; text: string; pictures?: string[] }[]
      phase: string
    }>('GET', `/api/factory/runs/${encodeURIComponent(name)}/conversation?after=${after}`)
  }
  async factorySend(name: string, text: string, images: { media_type: string; data: string }[]) {
    await this.req<{ ok: boolean }>('POST', `/api/factory/runs/${encodeURIComponent(name)}/messages`, {
      text,
      images,
    })
  }
  async factoryPicture(name: string, file: string) {
    const token = await this.tokenProvider()
    if (!token) throw new AuthError()
    let r: Response
    try {
      r = await this.fetchFn(
        `${this.base}/api/factory/runs/${encodeURIComponent(name)}/pictures/${encodeURIComponent(file)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
    } catch (e) {
      throw new NetworkError((e as Error).message)
    }
    if (r.status === 401) throw new AuthError()
    if (!r.ok) throw new NetworkError(`picture: ${r.status}`)
    return r.blob()
  }
  factoryPreview(name: string, to?: string) {
    return this.req<{ url: string; state: string }>(
      'POST',
      `/api/factory/runs/${encodeURIComponent(name)}/preview`,
      to ? { to } : {},
    )
  }
  factoryTry(projectId: string) {
    return this.req<{ id: string; name: string; url: string; state: string }>('POST', '/api/factory/try', {
      project_id: projectId,
    })
  }
  factoryPrototype(name: string, description: string) {
    return this.req<{ repo: string; url: string }>('POST', '/api/factory/prototypes', { name, description })
  }
  async factoryPreviewStop(name: string) {
    await this.req<void>('DELETE', `/api/factory/runs/${encodeURIComponent(name)}/preview`)
  }
  async putBlob(file: Blob) {
    const token = await this.tokenProvider()
    if (!token) throw new AuthError()
    const device = await this.deviceProvider().catch(() => undefined)
    let r: Response
    try {
      r = await this.fetchFn(`${this.base}${isMember() ? '/api/member/blob' : '/api/blobs'}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/octet-stream',
          ...(device ? { 'X-Device-Id': device } : {}),
        },
        body: file,
      })
    } catch (e) {
      throw new NetworkError((e as Error).message)
    }
    if (r.status === 401) throw new AuthError()
    if (!r.ok) {
      let reason = ''
      try {
        reason = String(((await r.json()) as { error?: string }).error ?? '')
      } catch {
        /* not JSON */
      }
      throw new NetworkError(reason || `upload: ${r.status}`)
    }
    return (await r.json()) as BlobRef
  }
  async blob(id: string) {
    const token = await this.tokenProvider()
    if (!token) throw new AuthError()
    let r: Response
    try {
      r = await this.fetchFn(`${this.base}${isMember() ? '/api/member/blob' : '/api/blobs'}/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch (e) {
      throw new NetworkError((e as Error).message)
    }
    if (r.status === 401) throw new AuthError()
    if (!r.ok) throw new NetworkError(`blob: ${r.status}`)
    return r.blob()
  }
  reviewPull(repo: string, number: number) {
    return this.req<ReviewPull>('GET', `/api/review/pr?repo=${encodeURIComponent(repo)}&number=${number}`)
  }
  reviewSubmit(repo: string, number: number, kind: 'approve' | 'request_changes' | 'comment', body: string) {
    return this.req<void>('POST', '/api/review/pr/review', { repo, number, kind, body })
  }
  reviewMerge(repo: string, number: number, style: 'squash' | 'merge' | 'rebase' = 'squash', card = false) {
    return this.req<void>('POST', '/api/review/pr/merge', { repo, number, style, ...(card ? { card } : {}) })
  }
  mergeQueue(suggestionId: string) {
    return this.req<{ state: 'queued' | 'merging' | 'merged' }>('POST', '/api/merges', {
      suggestion: suggestionId,
    })
  }
  reviewWalkthrough(repo: string, number: number) {
    return this.req<void>('POST', '/api/review/pr/walkthrough', { repo, number })
  }
  factoryTranscript(name: string, attempt?: number) {
    return this.req<{ lines: string[] }>(
      'GET',
      `/api/factory/runs/${encodeURIComponent(name)}/transcript${attempt ? `?attempt=${attempt}` : ''}`,
    )
  }
  /** the same door the iOS Shortcut uses: the server files it (with the assistant, when triage is on) */
  capture(text: string, from = 'app') {
    return this.req<{ ok: boolean; task: Resource }>(
      'POST',
      `/api/capture?${new URLSearchParams({ from })}`,
      { text },
    )
  }
  focusRecord(session: FocusSessionInput) {
    return this.req<void>('POST', '/api/focus/sessions', session)
  }
  focusSessions(days = 7) {
    return this.req<{ sessions: FocusSession[] }>('GET', `/api/focus/sessions?days=${days}`)
  }
  libraryAdd(item: LibraryAdd) {
    return this.req<{ item: LibraryItem }>('POST', '/api/library', item)
  }
  libraryPatch(id: string, patch: LibraryPatch) {
    return this.req<{ item: LibraryItem }>('PATCH', `/api/library/${encodeURIComponent(id)}`, patch)
  }
  libraryRemove(id: string) {
    return this.req<void>('DELETE', `/api/library/${encodeURIComponent(id)}`)
  }
  pushTest(deviceId?: string) {
    return this.req<void>('POST', '/api/push/test', deviceId ? { device_id: deviceId } : {})
  }
  now() {
    return this.req<NowResponse>('GET', '/api/now')
  }
  config() {
    return this.req<ServerConfig>('GET', '/api/config')
  }
  export() {
    return this.req<unknown>('GET', '/api/export')
  }
  assistantAsk(req: AssistantAskRequest) {
    return this.req<{ suggestions: Resource[]; text?: string }>('POST', '/api/assistant/ask', req)
  }
  agentStart(req: AgentStartRequest) {
    return this.req<{ session: Resource }>('POST', '/api/agents/sessions', req)
  }
  async agentSend(sessionId: string, content: string, opts?: SendOpts) {
    // with the blob store's phase 2 on, pictures go up first and ride the message
    // as ids; a failed upload throws before anything is sent, so the composer keeps its draft
    const images = opts?.images?.length ? await picturesToSend(opts.images, (b) => this.putBlob(b)) : []
    return this.req<{ session: Resource }>('POST', `/api/agents/sessions/${sessionId}/messages`, {
      content,
      ...(opts?.capture ? { capture: true } : {}),
      ...(images.length ? { images } : {}),
    })
  }
  agentToolResults(sessionId: string, results: AgentToolResult[]) {
    return this.req<{ session: Resource }>('POST', `/api/agents/sessions/${sessionId}/tools`, { results })
  }
  agentPrime(workspace: string) {
    return this.req<{ session: Resource }>(
      'GET',
      `/api/agents/prime?workspace=${encodeURIComponent(workspace)}`,
    )
  }
}
